import type { DirectusCampus, DirectusCategory, DirectusLocation } from './types';

const baseUrl = import.meta.env.VITE_DIRECTUS_URL.replace(/\/+$/, '');

async function getItems<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  const res = await fetch(`${baseUrl}${path}`, { signal });
  if (!res.ok) {
    throw new Error(`Directus responded ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { data?: T[] };
  if (!Array.isArray(body.data)) {
    throw new Error('Directus response had no data array');
  }
  return body.data;
}

export function fetchLocations(signal?: AbortSignal): Promise<DirectusLocation[]> {
  // limit=-1: Directus returns only 100 rows by default, which silently dropped most parking
  // lots in the previous version of this app.
  // fields=*: naming a field that doesn't exist yet (e.g. the wayfinding fields on an
  // unmigrated instance) makes Directus 9 reject the whole request.
  return getItems('/items/locations?limit=-1&fields=*', signal);
}

/**
 * Optional collections added by scripts/directus-migrate.mjs. Resolve to null (use the
 * built-in lists) when the collection doesn't exist, isn't public, or is empty.
 */
async function optional<T>(path: string, signal?: AbortSignal): Promise<T[] | null> {
  try {
    const rows = await getItems<T>(path, signal);
    return rows.length ? rows : null;
  } catch {
    return null;
  }
}

export const fetchCampuses = (signal?: AbortSignal) =>
  optional<DirectusCampus>('/items/campuses?limit=-1&sort=sort,name', signal);

export const fetchCategories = (signal?: AbortSignal) =>
  optional<DirectusCategory>('/items/categories?limit=-1&sort=sort,label', signal);

/** URL for a directus_files asset; with a width, Directus resizes it server-side. */
export function assetUrl(fileId: string, width?: number): string {
  const url = `${baseUrl}/assets/${encodeURIComponent(fileId)}`;
  return width ? `${url}?width=${width}&quality=80&fit=inside` : url;
}
